import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  DialogContent,
} from '@mui/material';
import * as React from 'react';

import { useSxStyles } from './useStyles';

export interface ListItemComponentProps<LI, LICustomActions> {
  listItem: LI;
  isTabDisabled?: boolean;
  customActions?: LICustomActions;
}

export interface ListItemInfoComponentProps<LI> {
  listItem: LI;
}

export interface Props<LI, LICustomActions> {
  listItems?: LI[];
  isTabDisabled?: boolean;
  customActions?: LICustomActions;
}

type AllListDialogContentComponentFactoryPropsType<LI, LICustomActions> = {
  ListItemComponent: React.ComponentType<
    ListItemComponentProps<LI, LICustomActions>
  >;
  getIdentifier: (item: LI) => string | number;
  ListItemInfoComponent?: React.ComponentType<ListItemInfoComponentProps<LI>>;
};

export const AllListDialogContentComponentFactory = <
  LI extends {},
  LICustomActions extends {},
>(
  props: AllListDialogContentComponentFactoryPropsType<LI, LICustomActions>,
) => {
  const { ListItemComponent, getIdentifier, ListItemInfoComponent } = props;

  const AllListDialogContent: React.FC<Props<LI, LICustomActions>> = ({
    listItems,
    isTabDisabled,
    customActions,
  }) => {
    const sxStyles = useSxStyles();

    return (
      <DialogContent
        sx={
          ListItemInfoComponent
            ? sxStyles.dialogContentExpansionPanel
            : sxStyles.dialogContentSimpleList
        }
      >
        {listItems && (
          <div data-cy="list">
            {listItems.map((listItem) =>
              ListItemInfoComponent ? (
                <div data-cy="list-item" key={getIdentifier(listItem)}>
                  <Accordion key={getIdentifier(listItem)}>
                    <AccordionSummary
                      expandIcon={<ExpandMore data-cy="expansion-icon" />}
                      sx={{
                        root: sxStyles.expansionPanelSummaryRoot,
                        content: sxStyles.expansionPanelSummaryContent,
                      }}
                    >
                      <ListItemComponent
                        listItem={listItem}
                        isTabDisabled={isTabDisabled}
                        customActions={customActions}
                      />
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{ root: sxStyles.expansionPanelDetailsRoot }}
                    >
                      <ListItemInfoComponent listItem={listItem} />
                    </AccordionDetails>
                  </Accordion>
                </div>
              ) : (
                <div data-cy="list-item" key={getIdentifier(listItem)}>
                  <ListItemComponent
                    key={getIdentifier(listItem)}
                    listItem={listItem}
                    isTabDisabled={isTabDisabled}
                    customActions={customActions}
                  />
                </div>
              ),
            )}
          </div>
        )}
      </DialogContent>
    );
  };

  return AllListDialogContent;
};
